/**
 * Utility functions for TotalsRow: localStorage management, schema validation,
 * and aggregation value formatting.
 */

import { formatTime, type TimeFormat } from '@/lib/time';
import { formatCurrency, getCachedDecimalFormatter } from '@/utils/currency';

import type { PropertySchemaMap } from '../../hooks/usePropertySchema';
import type { AggregationValue } from '../hooks/useColumnAggregations';

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

/**
 * Read aggregation selections from localStorage.
 * Returns {} on any error (e.g. corrupted or missing JSON).
 */
export function loadSelectionsFromStorage(
  storageKey: string
): Record<string, string> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {};
    }
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(
      parsed as Record<string, unknown>
    )) {
      if (typeof value === 'string') {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Schema validation
// ---------------------------------------------------------------------------

/**
 * Validate raw localStorage selections against the current schema.
 *
 * Drops:
 * - Stale properties (property_type_name no longer in schema)
 * - Unsupported aggregations (field type changed; aggregation no longer allowed)
 */
export function validateSelections(
  raw: Record<string, string>,
  schema: PropertySchemaMap,
  fieldTypeAggregations: Record<string, string[]>
): Record<string, string> {
  const valid: Record<string, string> = {};
  for (const [propName, aggFn] of Object.entries(raw)) {
    const propSchema = schema[propName];
    if (!propSchema) {
      continue;
    }
    const allowedAggs = fieldTypeAggregations[propSchema.field_type_name] ?? [];
    if (!allowedAggs.includes(aggFn)) {
      continue;
    }
    valid[propName] = aggFn;
  }
  return valid;
}

// ---------------------------------------------------------------------------
// Value formatting
// ---------------------------------------------------------------------------

/**
 * Format an aggregation value for display.
 *
 * - `number`: formatted with en-US locale
 * - `string`: assumed to be an ISO date or datetime — formatted with locale
 * - `Array<{amount, unit}>`: each item formatted with currency symbol, joined with " + "
 * - `{amount, unit}` single object (returned when `convert_to` is set): one converted currency amount
 * - `null` / empty array: "—"
 */
export function formatAggregationValue(
  value: AggregationValue,
  timeFormat: TimeFormat,
  timezone: string
): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'number') {
    return getCachedDecimalFormatter(0, 2).format(value);
  }
  if (typeof value === 'string') {
    return formatDateValue(value, timeFormat, timezone);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '—';
    }
    return value.map(formatMoney).join(' + ');
  }
  return formatMoney(value);
}

/**
 * Format a single {amount, unit} pair as currency, via the shared `formatCurrency` util.
 * If `unit` is null or empty, returns the amount formatted as a plain number (no currency symbol).
 */
function formatMoney({
  amount,
  unit,
}: {
  amount: number;
  unit: string | null;
}): string {
  if (!Number.isFinite(amount)) {
    return '—';
  }
  if (!unit) {
    return getCachedDecimalFormatter(0, 2).format(amount);
  }
  return formatCurrency(amount, unit);
}

/**
 * Format an ISO date or datetime string for display in the totals row.
 *
 * - Date-only ("2025-01-15"): "Jan 15, 2025" — always UTC (a date-only value
 *   has no wall-clock time to localize).
 * - Datetime ("2025-01-15T10:30:00+00:00"): "Jan 15, 2025, 10:30 AM EST" (12h)
 *   or "Jan 15, 2025, 10:30 EST" (24h), rendered in `timezone` with a visible
 *   timezone label (FRONTEND_PATTERNS.md § Timezones).
 */
function formatDateValue(
  isoString: string,
  timeFormat: TimeFormat,
  timezone: string
): string {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    return isoString;
  }
  // Date-only strings have no "T" separator
  const isDateOnly = !isoString.includes('T');
  if (isDateOnly) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  return formatTime(date, timeFormat, {
    timeZone: timezone,
    locale: 'en-US',
    dateParts: {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZoneName: 'short',
    },
  });
}
